import {
    Alert,
    Icons,
    Stack,
    ThemeSpaceVariable,
    ModalCloseButton,
    Divider,
} from "@caspeco/casper-ui-library";
// eslint-disable-next-line caspeco-react/discourage-chakra-import
import { ModalHeader, ModalBody, ModalFooter } from "@chakra-ui/react";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import ActivityStepContinueButton from "components/selection-components/select-activity/activity-step-continue-button";
import Summary from "components/selection-components/summary/summary";
import { SlideModal, SlideDirection } from "components/slide-modal/slide-modal";
import { usePaymentRule } from "hooks/use-payment-rule";
import { PageType } from "logger/page-tracking";
import { usePageTracking } from "logger/use-page-tracking";
import { useStepTracking } from "logger/use-step-tracking";
import { TrackingStepName } from "logger/user-tracking";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PaymentRule } from "types/payment";

interface ISummary {
    isOpen: boolean;
    onClose: () => void;
    gotoDetailsForm: () => void;
    openActivity: (ruleId: number) => void;
    openExperience: (experienceId: number) => void;
}

function SummaryModal({
    openActivity,
    openExperience,
    onClose,
    isOpen,
    gotoDetailsForm,
}: ISummary) {
    const { data: reservation } = useWebBooking();
    const { t } = useTranslation();
    const paymentRule = usePaymentRule();
    const [slideDirection, setSlideDirection] = useState<SlideDirection>("bottom");

    // Track summary modal when opened (legacy step tracking)
    useStepTracking({
        stepName: TrackingStepName.SummaryModal,
        autoTrack: isOpen,
        dependencies: [isOpen],
    });

    usePageTracking({
        pageName: "BookingSummary",
        pageType: PageType.Modal,
        metadata: {
            bookedActivitiesCount: reservation?.getBookTimes()?.length || 0,
            paymentRule: paymentRule,
        },
        autoTrack: isOpen,
        dependencies: [isOpen],
    });

    if (!reservation) return null;

    const headerDate = t("time.date", { value: reservation.start.unix() });

    function getAlertText() {
        switch (paymentRule) {
            case PaymentRule.Prepayment:
                return t("activitySummary.alert.prepayment");
            case PaymentRule.NoShowFee:
                return t("activitySummary.alert.paymentCardRegistration");
            default:
                return t("activitySummary.alert.paymentAtLocation");
        }
    }

    function handleClose() {
        setSlideDirection("bottom");
        onClose();
    }

    function handleConfirm() {
        setSlideDirection("bottom");
        gotoDetailsForm();
    }

    return (
        <SlideModal
            isOpen={isOpen}
            onClose={handleClose}
            slideDirection={slideDirection}
            variant="fullscreen"
        >
            <ModalHeader
                display="flex"
                justifyContent="center"
                p={ThemeSpaceVariable.Medium}
                fontWeight={"400"}
            >
                {t("activitySummary.title", { date: headerDate })}
                <ModalCloseButton isAbsolutePositioned />
            </ModalHeader>
            <ModalBody px={ThemeSpaceVariable.Medium} pb={ThemeSpaceVariable.Medium}>
                <Summary
                    openActivity={openActivity}
                    openExperience={openExperience}
                    onCloseModal={handleClose}
                />
                <Divider />
                <Stack paddingTop={ThemeSpaceVariable.Large} gap={ThemeSpaceVariable.Medium}>
                    <Alert type="info" icon={Icons.PaymentCard}>
                        {getAlertText()}
                    </Alert>
                </Stack>
            </ModalBody>
            <ModalFooter
                p={ThemeSpaceVariable.Medium}
                bg="transparent"
                position="sticky"
                bottom="0"
            >
                <ActivityStepContinueButton onClick={handleConfirm} />
            </ModalFooter>
        </SlideModal>
    );
}

export default SummaryModal;
